import { useContext, useMemo, useState } from "react";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import {
  BotFormType,
  EditBotOption,
} from "@shared/features/bots/BotForm/common/types";
import { sharedLib } from "@shared/services/shared.service";
import {
  BaseBotData,
  BotConfigurationType,
  BotStatus,
  BotType,
  DisplayResultMessage,
  EditBotConfigurationRequest,
  EditBotRequest,
  OpenMessage,
  LanguageNameSpaces,
} from "@shared/types";
import { BOT_TYPE_TO_DISPLAY_NAME } from "@shared/features/bots/constants";
import { getBotValues } from "@shared/features/bots/BotForm/common/utils";
import { useCompareBotValues } from "@shared/features/bots/BotForm/common/hooks";
import { UseQuotaModal } from "@shared/features/settings/types";
import { BOT_TYPE_TO_USAGE_TYPE } from "@shared/features/settings/constants";
import { useClientsData, useExchangesMetadata } from "@shared/utils/hooks/misc";
import {
  LEVERAGE_EXCHANGES_TAG,
  NON_LEVERAGE_EXCHANGES_TAG,
} from "@shared/common/constants";

interface UseSaveBotButtonProps {
  botId?: number;
  disabled?: boolean;
  errorMessage?: string;
  copiedFrom?: number;
  trackSaveClick?: () => void;
  displayResultMessage: DisplayResultMessage;
  onSuccess?: () => void;
  openMessage?: OpenMessage;
  openAmountWarningConfirmModal?: any;
  onSaveClick?: () => Promise<void> | void;
  onDisabledClick?: () => void;
  useQuotaModal: UseQuotaModal;
}

const useSaveBotButton = ({
  botId,
  disabled,
  errorMessage,
  copiedFrom,
  trackSaveClick,
  displayResultMessage,
  onSuccess,
  openMessage,
  openAmountWarningConfirmModal,
  onSaveClick: propsOnSaveClick,
  onDisabledClick,
  useQuotaModal,
}: UseSaveBotButtonProps) => {
  const {
    parsedEntries,
    botFormType,
    bulkActionValidBotsData,
    bulkActionInvalidBotsData,
    botType,
    isChannelConfiguration,
    botGeneralData,
    symbolPriceData,
    getParsedFormData,
    getParsedBulkEditFormData,
    configurationType,
    subGroupId,
  } = useContext(BotFormContext)!;
  const { getAccountById } = useClientsData();
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmBulkActionModalOpen, setConfirmBulkActionModalOpen] =
    useState(false);
  const [createBots, createBotsStatus] =
    sharedLib.sharedApi.bots.useCreateBotsMutation();
  const [editBot, editBotStatus] =
    sharedLib.sharedApi.bots.useEditBotMutation();
  const [editBots, editBotsStatus] =
    sharedLib.sharedApi.bots.useEditBotsMutation();
  const [
    setSubGroupDefaultConfiguration,
    setSubGroupDefaultConfigurationStatus,
  ] = sharedLib.sharedApi.bots.useSetSubGroupDefaultConfigurationMutation();

  const { compareValues } = useCompareBotValues();
  const { getIsFree } = useExchangesMetadata();

  const isBulkEdit = botFormType === BotFormType.BULK_EDIT;
  const isBulkCreate = botFormType === BotFormType.BULK_CREATE;
  const isBulkAction = isBulkEdit || isBulkCreate;
  const isEdit = botFormType === BotFormType.EDIT;
  const isCreate = !isChannelConfiguration && !botId && !isBulkEdit;
  const botData = createBotsStatus.data?.[0] || editBotStatus.data;
  const isGridBot = botType === BotType.GRID;
  const botStatus =
    isEdit && botGeneralData && "status" in botGeneralData
      ? botGeneralData.status
      : undefined;
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const botTypeDisplay = t(BOT_TYPE_TO_DISPLAY_NAME[botType]);
  const isHedgingBot = botType === BotType.HEDGING;

  const botsQuotaData = useQuotaModal({
    usageType: BOT_TYPE_TO_USAGE_TYPE[botType],
  });

  const getBotValuesChanged = () => {
    const [
      originalBotValues,
      originalBotCheckedFields,
      originalOverrideFields,
    ] = botGeneralData
      ? getBotValues(botType, botGeneralData as BaseBotData, {
          pricePrecision: symbolPriceData?.precision.price,
        })
      : [];
    return compareValues(
      undefined,
      originalBotValues,
      originalBotCheckedFields,
      originalOverrideFields,
      ["account", "name"]
    );
  };

  const gridBotValuesChanged =
    isGridBot && isEdit ? getBotValuesChanged() : false;

  const amountWarningTexts = useMemo(() => {
    if (isGridBot || isHedgingBot) return;
    const numWarningOrders = parsedEntries?.filter(
      (entry) => entry.availableAmountWarning
    ).length;
    if (numWarningOrders) {
      return [
        t("messages.errors.bot_high_amount_line1"),
        t("messages.errors.bot_high_amount_line2"),
      ];
    }
  }, [parsedEntries, isGridBot, isHedgingBot]);

  const saveChanges = async (
    activate?: boolean,
    editBotOption?: EditBotOption,
    botName?: string
  ) => {
    const promise = propsOnSaveClick?.();
    let botData = getParsedFormData?.();
    trackSaveClick?.();
    if (botData) {
      botData.copied_from = copiedFrom;
      if (activate !== undefined) {
        botData.status = activate ? BotStatus.ACTIVE : BotStatus.INACTIVE;
      }
      if (isChannelConfiguration) botData.is_channel_configuration = true;
      if (botName) botData.dca_bot_name = botName;
      let action: any = createBots;
      if (isChannelConfiguration) action = setSubGroupDefaultConfiguration;
      else if (!isCreate) action = editBot;
      if (isChannelConfiguration) {
        botData = {
          ...botData,
          is_channel_configuration: true,
          exchange:
            configurationType === BotConfigurationType.SPOT
              ? NON_LEVERAGE_EXCHANGES_TAG
              : LEVERAGE_EXCHANGES_TAG,
          dca_bot_name: "test name",
          sub_group_ids: [subGroupId],
        } as EditBotConfigurationRequest;
      } else if (!isCreate) {
        botData = {
          ...botData,
          dca_bot_id: botId,
        } as EditBotRequest;
        if (editBotOption) {
          (botData as EditBotRequest).should_display_edited_bot =
            editBotOption === EditBotOption.CREATE_NEW;
        }
      }
      let isQuotaError: boolean | undefined | void = false;
      const accountIds = botData.account_ids;
      const quotaAccountIds = Array.isArray(accountIds)
        ? accountIds.filter((accountId) => {
            const account = getAccountById(accountId);
            return account && !getIsFree(account.exchange);
          })
        : undefined;

      if (activate && !isChannelConfiguration) {
        const closeModal = confirmModalOpen
          ? () => setConfirmModalOpen(false)
          : undefined;
        if (!quotaAccountIds?.length) {
          closeModal?.();
        } else {
          isQuotaError = await botsQuotaData.handleTriggerClick({
            closeModal,
            numItems: quotaAccountIds?.length,
          });
        }
      }

      const createOrEdit = async () => {
        const successMessage = isChannelConfiguration
          ? t("messages.success.channel_configuration_edited")
          : isCreate
          ? t("messages.success.bot_created_with_type", {
              botType: botTypeDisplay,
            })
          : t("messages.success.bot_edited_with_type", {
              botType: botTypeDisplay,
            });
        return displayResultMessage(
          action(botData as any).unwrap(),
          successMessage
        ).then((response) => {
          if (!confirmModalOpen) onSuccess?.();
          return response;
        });
      };

      if (!isQuotaError) {
        if (!promise) {
          return createOrEdit();
        } else {
          return promise.then(() => createOrEdit());
        }
      } else {
        return Promise.reject();
      }
    }
  };

  const onBulkEditSaveClick = () => {
    propsOnSaveClick?.();
    const botData = getParsedBulkEditFormData?.();
    trackSaveClick?.();
    if (botData) {
      const botTypeDisplay = t(BOT_TYPE_TO_DISPLAY_NAME[botType]);
      displayResultMessage(
        editBots(botData as any).unwrap(),
        `${botTypeDisplay} bots successfully edited`
      ).then(() => {
        onSuccess?.();
      });
    }
  };

  const openConfirmModal = () => {
    const isActive = botStatus === BotStatus.ACTIVE;
    if (
      !isChannelConfiguration &&
      (isCreate || !isActive || (isGridBot && gridBotValuesChanged))
    ) {
      setConfirmModalOpen(true);
    } else {
      saveChanges().catch(() => {});
    }
  };

  const onSaveChangesClick = () => {
    if (disabled) {
      onDisabledClick?.();
      errorMessage && openMessage?.(errorMessage, "warning");
    } else if (isBulkAction && !confirmBulkActionModalOpen) {
      setConfirmBulkActionModalOpen(true);
    } else if (amountWarningTexts) {
      openAmountWarningConfirmModal(amountWarningTexts, openConfirmModal);
    } else {
      openConfirmModal();
    }
  };

  const isLoading =
    createBotsStatus.isLoading ||
    editBotStatus.isLoading ||
    editBotsStatus.isLoading ||
    setSubGroupDefaultConfigurationStatus.isLoading ||
    botsQuotaData.isLoading;
  const activatingBot =
    createBotsStatus.originalArgs?.status === BotStatus.ACTIVE ||
    editBotStatus.originalArgs?.status === BotStatus.ACTIVE;

  const actionDisplay = isCreate ? t("buttons.create") : t("buttons.save");
  const actionButtonDisplay = isCreate
    ? (bulkActionValidBotsData?.length || 1) > 1
      ? t("buttons.create_bots")
      : t("buttons.create_bot")
    : t("buttons.save_changes");

  const confirmModalData = {
    isOpen: confirmModalOpen,
    close: () => setConfirmModalOpen(false),
    actionDisplay,
    activatingBot,
  };

  const onConfirmBulkActionClick = () => {
    if (isBulkCreate) {
      setConfirmBulkActionModalOpen(false);
      onSaveChangesClick();
    } else {
      onBulkEditSaveClick();
    }
  };

  const confirmBulkEditModalData = {
    title: isBulkEdit
      ? t("summary.bulk_edit_modal.confirm_bulk_edit")
      : t("summary.bulk_edit_modal.confirm_bulk_create"),
    isOpen: confirmBulkActionModalOpen,
    onClose: () => setConfirmBulkActionModalOpen(false),
    onOk: onConfirmBulkActionClick,
    content: [
      t("summary.bulk_edit_modal.content.latest_validation"),
      t("summary.bulk_edit_modal.content.are_you_sure"),
    ],
    numValidBots: bulkActionValidBotsData?.length || 0,
    numInvalidBots: bulkActionInvalidBotsData?.length || 0,
  };

  return {
    isLoading,
    onSaveChangesClick,
    saveChanges,
    actionButtonDisplay,
    botData,
    confirmModalData,
    openConfirmModal,
    confirmBulkEditModalData,
    gridBotValuesChanged,
    botsQuotaModal: botsQuotaData.quotaModal,
  };
};

export default useSaveBotButton;
